/* ==========================================================
   SPINDLE ANNOUNCEMENT BAR
   Optimized / Maintenance-Friendly Version
   ========================================================== */


/* ==========================================================
   CUSTOMIZE HERE

   Most settings you may want to change later are here.
   Times are in milliseconds unless otherwise noted.

   1000 = 1 second
   ========================================================== */

/* ==========================================================
   THEME EDITOR VALUE BRIDGE

   This only replaces the numeric values from CUSTOMIZE HERE
   with values passed by the Liquid section. Slider behavior,
   timing flow, DOM handling, swipe behavior, transitions, and
   one-announcement-at-a-time logic below are untouched.
   ========================================================== */

function getAnnouncementThemeSetting(name, fallback) {

  const bar =
    document.querySelector(
      "announcement-bar.wt-announcement-bar"
    );

  if (!bar) {
    return fallback;
  }

  const value =
    Number.parseFloat(
      bar.dataset[name]
    );

  return Number.isFinite(value)
    ? value
    : fallback;
}


function getAnnouncementTransitionDuration(fallback) {

  const bar =
    document.querySelector(
      "announcement-bar.wt-announcement-bar"
    );

  if (!bar) {
    return fallback;
  }

  const preset =
    String(
      bar.dataset.animationSpeedPreset || "100"
    );

  /* Keep your existing 450ms timing as Normal. */
  if (preset === "150") {
    return Math.round(fallback * 1.5);
  }

  if (preset === "50") {
    return Math.round(fallback * 0.6);
  }

  return fallback;
}


const ANNOUNCEMENT_CONFIG = {

  /* Mobile / Desktop breakpoint */
  breakpointPx: 900,


  /* ========================================================
     MOBILE SETTINGS
     ======================================================== */

  mobile: {

    /*
     * How long the BEGINNING of a long message
     * stays still before scrolling.
     */
    get startPauseMs() {
      return getAnnouncementThemeSetting(
        "mobileStartPauseMs",
        3000
      );
    },


    /*
     * How long the END of a long message
     * stays still after scrolling.
     */
    get endPauseMs() {
      return getAnnouncementThemeSetting(
        "mobileEndPauseMs",
        3000
      );
    },


    /*
     * Long-text scrolling speed.
     *
     * Higher = faster
     *
     * 40 = slow
     * 55 = medium
     * 70 = fast
     * 90 = very fast
     */
    get scrollSpeedPxPerSec() {
      return getAnnouncementThemeSetting(
        "mobileScrollSpeed",
        70
      );
    },


    /*
     * If the entire mobile message already fits,
     * how long should it remain visible?
     */
    get shortMessageDisplayMs() {
      return getAnnouncementThemeSetting(
        "mobileShortDisplayMs",
        6000
      );
    },


    /*
     * Pause after the message is finished before
     * beginning the quick #1 -> #2 transition.
     */
    get betweenMessagesPauseMs() {
      return getAnnouncementThemeSetting(
        "mobileBetweenPauseMs",
        500
      );
    },


    /*
     * Quick slide transition speed between
     * announcement #1 -> #2.
     */
    get transitionDurationMs() {
      return getAnnouncementTransitionDuration(450);
    },


    /*
     * Transition motion.
     */
    transitionEasing: "cubic-bezier(0.4, 0, 0.2, 1)",


    /*
     * Ignore tiny width measurement differences.
     */
    overflowTolerancePx: 2
  },


  /* ========================================================
     DESKTOP SETTINGS
     ======================================================== */

  desktop: {

    /*
     * How long each desktop announcement
     * remains visible.
     */
    get displayTimeMs() {
      return getAnnouncementThemeSetting(
        "desktopDisplayMs",
        6000
      );
    },


    /*
     * Speed of #1 -> #2 desktop transition.
     */
    get transitionDurationMs() {
      return getAnnouncementTransitionDuration(450);
    },


    /*
     * Smooth transition without the "wave" feeling.
     */
    transitionEasing: "cubic-bezier(0.4, 0, 0.2, 1)",


    /*
     * IMPORTANT:
     *
     * Disable the theme's original animation/transition
     * while our custom desktop slider is active.
     *
     * Keep this TRUE.
     */
    disableThemeMotion: true
  },


  /* ========================================================
     TOUCH / RESIZE
     ======================================================== */

  interaction: {

    /*
     * Minimum finger movement required
     * to register a swipe.
     */
    swipeThresholdPx: 30,


    /*
     * Delay before recalculating layout
     * after browser resize.
     */
    resizeDebounceMs: 150
  },


  /* ========================================================
     CLOSE BUTTON
     ======================================================== */

  close: {

    storageKey: "wt-announcement-hidden",


    /*
     * How long announcement remains hidden
     * after pressing X.
     *
     * Current = 24 hours.
     */
    hideDurationMs:
      24 * 60 * 60 * 1000
  },


  /* ========================================================
     HTML SELECTORS
     Usually do NOT change these.
     ======================================================== */

  selectors: {

    container:
      "#wt-announcement__container",

    slide:
      ".wt-announcement__body",

    message:
      ".wt-announcement__message",

    closeButton:
      ".wt-announcement__close"
  },


  /* ========================================================
     CSS CLASSES
     Usually do NOT change these.
     ======================================================== */

  classes: {

    mobileActive:
      "is-mobile-active",

    mobileSwitching:
      "is-mobile-switching",

    desktopActive:
      "is-desktop-active"
  }
};



class AnnouncementBar extends HTMLElement {

  constructor() {

    super();


    /* ======================================================
       ELEMENTS
       ====================================================== */

    this.announcementBar = null;

    this.closeButton = null;


    /* ======================================================
       ANNOUNCEMENTS
       ====================================================== */

    this.allSlides = [];

    this.mobileSlides = [];

    this.desktopSlides = [];


    /* ======================================================
       CURRENT ANNOUNCEMENT
       ====================================================== */

    this.currentIndex = 0;

    this.desktopIndex = 0;

    this.mode = null;


    /* ======================================================
       TOUCH
       ====================================================== */

    this.touchStartX = 0;

    this.touchEndX = 0;


    /* ======================================================
       TIMERS
       ====================================================== */

    this.restartTimer = null;

    this.desktopTimer = null;

    this.resizeTimer = null;


    /* ======================================================
       ANIMATIONS
       ====================================================== */

    this.mobileAnimation = null;

    this.mobileTransitionAnimations = [];

    this.desktopTransitionAnimations = [];


    /*
     * Tokens prevent old animation callbacks from
     * changing a newer animation state.
     */

    this.mobileTransitionToken = 0;

    this.desktopTransitionToken = 0;


    /* ======================================================
       ORIGINAL THEME MOTION BACKUP

       Used so we can temporarily disable the theme's
       original animation on desktop, then restore it.
       ====================================================== */

    this.desktopMotionBackup =
      new Map();


    /* ======================================================
       BIND EVENTS
       ====================================================== */

    this.handleResize =
      this.handleResize.bind(this);


    this.handleTouchStart =
      this.handleTouchStart.bind(this);


    this.handleTouchMove =
      this.handleTouchMove.bind(this);


    this.handleTouchEnd =
      this.handleTouchEnd.bind(this);


    this.hideForOneDay =
      this.hideForOneDay.bind(this);
  }



  /* ========================================================
     INITIALIZE
     ======================================================== */

  connectedCallback() {

    this.announcementBar =
      this.querySelector(
        ANNOUNCEMENT_CONFIG
          .selectors
          .container
      );


    this.closeButton =
      this.querySelector(
        ANNOUNCEMENT_CONFIG
          .selectors
          .closeButton
      );


    if (!this.announcementBar) {

      return;
    }


    /* Close button */

    if (this.closeButton) {

      this.closeButton.addEventListener(
        "click",
        this.hideForOneDay
      );
    }


    /* Mobile swipe */

    this.announcementBar.addEventListener(
      "touchstart",
      this.handleTouchStart,
      {
        passive: true
      }
    );


    this.announcementBar.addEventListener(
      "touchmove",
      this.handleTouchMove,
      {
        passive: true
      }
    );


    this.announcementBar.addEventListener(
      "touchend",
      this.handleTouchEnd,
      {
        passive: true
      }
    );


    /* Browser resize */

    window.addEventListener(
      "resize",
      this.handleResize
    );


    /*
     * Initialize correct mode.
     */

    this.setupAnnouncementBar(true);
  }



  /* ========================================================
     CLEANUP
     ======================================================== */

  disconnectedCallback() {

    this.clearAllTimers();

    this.cancelAllAnimations();

    this.restoreDesktopThemeMotion();


    if (this.closeButton) {

      this.closeButton.removeEventListener(
        "click",
        this.hideForOneDay
      );
    }


    if (this.announcementBar) {

      this.announcementBar.removeEventListener(
        "touchstart",
        this.handleTouchStart
      );


      this.announcementBar.removeEventListener(
        "touchmove",
        this.handleTouchMove
      );


      this.announcementBar.removeEventListener(
        "touchend",
        this.handleTouchEnd
      );
    }


    window.removeEventListener(
      "resize",
      this.handleResize
    );
  }



  /* ========================================================
     SHARED HELPERS
     ======================================================== */


  isMobileView() {

    return (
      window.innerWidth <
      ANNOUNCEMENT_CONFIG.breakpointPx
    );
  }



  getMode() {

    return this.isMobileView()
      ? "mobile"
      : "desktop";
  }



  getAllSlides() {

    return Array.from(
      this.querySelectorAll(
        ANNOUNCEMENT_CONFIG
          .selectors
          .slide
      )
    );
  }



  /*
   * Shopify/theme may duplicate announcement blocks.
   *
   * Keep only ONE copy of each announcement.
   */

  getUniqueSlides(
    slides = this.getAllSlides()
  ) {

    const seen =
      new Set();


    return slides.filter(
      (slide) => {

        const key =

          slide.dataset
            .announcementId ||

          slide.textContent
            .trim();


        if (seen.has(key)) {

          return false;
        }


        seen.add(key);

        return true;
      }
    );
  }



  normalizeIndex(
    index,
    length
  ) {

    if (!length) {

      return 0;
    }


    return (
      (index + length) %
      length
    );
  }



  /* ========================================================
     TIMER HELPERS
     ======================================================== */


  clearTimer(timerName) {

    if (!this[timerName]) {

      return;
    }


    clearTimeout(
      this[timerName]
    );


    this[timerName] = null;
  }



  clearAllTimers() {

    this.clearTimer(
      "restartTimer"
    );


    this.clearTimer(
      "desktopTimer"
    );


    this.clearTimer(
      "resizeTimer"
    );
  }



  /* ========================================================
     ANIMATION HELPERS
     ======================================================== */


  cancelAnimation(animation) {

    if (!animation) {

      return;
    }


    try {

      animation.onfinish = null;

      animation.cancel();

    } catch (error) {

      /*
       * Animation may already be finished.
       */
    }
  }



  cancelAnimationList(list) {

    list.forEach(
      (animation) => {

        this.cancelAnimation(
          animation
        );

      }
    );


    list.length = 0;
  }



  cancelMobileAnimations() {

    this.cancelAnimation(
      this.mobileAnimation
    );


    this.mobileAnimation = null;


    /*
     * Invalidate previous transition callback.
     */

    this.mobileTransitionToken += 1;


    this.cancelAnimationList(
      this.mobileTransitionAnimations
    );
  }



  cancelDesktopAnimations() {

    /*
     * Invalidate previous desktop transition.
     */

    this.desktopTransitionToken += 1;


    this.cancelAnimationList(
      this.desktopTransitionAnimations
    );
  }



  cancelAllAnimations() {

    this.cancelMobileAnimations();

    this.cancelDesktopAnimations();
  }



  /* ========================================================
     DESKTOP THEME MOTION CONTROL

     Prevents the Shopify theme's original animations from
     fighting with our JS animation.

     This is the main fix for the "wavy" desktop movement.
     ======================================================== */


  disableDesktopThemeMotion() {

    if (
      !ANNOUNCEMENT_CONFIG
        .desktop
        .disableThemeMotion
    ) {

      return;
    }


    /*
     * Already disabled.
     */

    if (
      this.desktopMotionBackup.size
    ) {

      return;
    }


    const targets =
      new Set();


    /*
     * Include announcement container itself.
     */

    targets.add(
      this.announcementBar
    );


    /*
     * Include every announcement and every element
     * inside the announcement.
     *
     * This prevents individual spans/strong/a elements
     * from performing the theme's own animations.
     */

    this.getAllSlides()
      .forEach(
        (slide) => {

          targets.add(slide);


          slide.querySelectorAll("*")
            .forEach(
              (element) => {

                targets.add(
                  element
                );

              }
            );

        }
      );


    /*
     * Save original inline styles,
     * then disable motion.
     */

    targets.forEach(
      (element) => {

        this.desktopMotionBackup.set(
          element,
          {

            animation:
              element.style.animation,

            transition:
              element.style.transition

          }
        );


        element.style.animation =
          "none";


        element.style.transition =
          "none";

      }
    );
  }



  restoreDesktopThemeMotion() {

    if (
      !this.desktopMotionBackup.size
    ) {

      return;
    }


    this.desktopMotionBackup.forEach(
      (
        styles,
        element
      ) => {

        element.style.animation =
          styles.animation;


        element.style.transition =
          styles.transition;

      }
    );


    this.desktopMotionBackup.clear();
  }



  /* ========================================================
     MOBILE HEIGHT LOCK

     Prevents announcement bar shrinking during transition.
     ======================================================== */


  lockMobileHeight() {

    if (!this.announcementBar) {

      return;
    }


    const outerHeight =
      this.getBoundingClientRect()
        .height;


    const innerHeight =
      this.announcementBar
        .getBoundingClientRect()
        .height;


    const height =
      Math.max(
        outerHeight,
        innerHeight
      );


    if (!height) {

      return;
    }


    this.style.height =
      `${height}px`;


    this.style.minHeight =
      `${height}px`;


    this.announcementBar
      .style
      .height =
      `${height}px`;


    this.announcementBar
      .style
      .minHeight =
      `${height}px`;
  }



  unlockMobileHeight() {

    this.style.height =
      "";


    this.style.minHeight =
      "";


    if (!this.announcementBar) {

      return;
    }


    this.announcementBar
      .style
      .height =
      "";


    this.announcementBar
      .style
      .minHeight =
      "";
  }



  /*
   * Give browser time to restore normal document flow
   * before removing fixed bar height.
   */

  unlockMobileHeightAfterLayout() {

    requestAnimationFrame(
      () => {

        requestAnimationFrame(
          () => {

            this.unlockMobileHeight();

          }
        );

      }
    );
  }



  /* ========================================================
     INLINE STYLE RESET HELPERS
     ======================================================== */


  resetSlideInlineStyles(slide) {

    const properties = [

      "display",
      "width",
      "maxWidth",
      "position",
      "top",
      "left",
      "height",
      "transform",
      "justifyContent",
      "alignItems",
      "willChange"

    ];


    properties.forEach(
      (property) => {

        slide.style[property] =
          "";

      }
    );
  }



  resetContainerInlineStyles() {

    if (!this.announcementBar) {

      return;
    }


    const properties = [

      "display",
      "width",
      "maxWidth",
      "height",
      "minHeight",
      "position",
      "overflow",
      "transform",
      "transition",
      "willChange"

    ];


    properties.forEach(
      (property) => {

        this.announcementBar
          .style[property] =
          "";

      }
    );
  }



  /* ========================================================
     MOBILE / DESKTOP MODE
     ======================================================== */


  setupAnnouncementBar(
    force = false
  ) {

    if (!this.announcementBar) {

      return;
    }


    const nextMode =
      this.getMode();


    /*
     * Browser resized but did not cross
     * mobile/desktop breakpoint.
     */

    if (
      !force &&
      nextMode === this.mode
    ) {

      /*
       * Mobile width changes can change
       * text overflow distance.
       */

      if (
        nextMode === "mobile" &&
        this.mobileSlides.length
      ) {

        this.showMobileAnnouncement(
          this.currentIndex
        );
      }


      return;
    }


    this.mode =
      nextMode;



    /* =======================
       MOBILE
       ======================= */

    if (
      nextMode === "mobile"
    ) {

      this.resetDesktopTicker();

      this.setupMobileTicker();

      return;
    }



    /* =======================
       DESKTOP
       ======================= */

    this.resetMobileTicker();

    this.setupDesktopTicker();
  }



  /* ========================================================
     WINDOW RESIZE
     ======================================================== */


  handleResize() {

    this.clearTimer(
      "resizeTimer"
    );


    this.resizeTimer =
      setTimeout(
        () => {

          const nextMode =
            this.getMode();


          const changedMode =
            nextMode !==
            this.mode;


          this.setupAnnouncementBar(
            changedMode
          );

        },

        ANNOUNCEMENT_CONFIG
          .interaction
          .resizeDebounceMs
      );
  }



  /* ========================================================
     MOBILE SETUP
     ======================================================== */


  setupMobileTicker() {

    this.clearTimer(
      "restartTimer"
    );


    this.cancelMobileAnimations();

    this.unlockMobileHeight();


    /*
     * Restore normal theme motion because
     * desktop-only suppression is no longer needed.
     */

    this.restoreDesktopThemeMotion();


    this.allSlides =
      this.getAllSlides();


    this.mobileSlides =
      this.getUniqueSlides(
        this.allSlides
      );


    this.announcementBar
      .classList
      .remove(
        ANNOUNCEMENT_CONFIG
          .classes
          .mobileSwitching
      );


    /*
     * Hide every mobile message first.
     */

    this.allSlides.forEach(
      (slide) => {

        slide.classList.remove(
          ANNOUNCEMENT_CONFIG
            .classes
            .mobileActive
        );

      }
    );


    this.currentIndex =
      0;


    if (
      this.mobileSlides.length
    ) {

      this.showMobileAnnouncement(
        0
      );
    }
  }



  /* ========================================================
     MOBILE MESSAGE DISPLAY
     ======================================================== */


  showMobileAnnouncement(
    index
  ) {

    if (
      !this.isMobileView() ||
      !this.mobileSlides.length
    ) {

      return;
    }


    this.clearTimer(
      "restartTimer"
    );


    /*
     * Cancel previous text scrolling.
     */

    this.cancelAnimation(
      this.mobileAnimation
    );


    this.mobileAnimation =
      null;


    /*
     * Hide all messages.
     */

    this.allSlides.forEach(
      (slide) => {

        slide.classList.remove(
          ANNOUNCEMENT_CONFIG
            .classes
            .mobileActive
        );

      }
    );


    /*
     * Set current message.
     */

    this.currentIndex =
      this.normalizeIndex(
        index,
        this.mobileSlides.length
      );


    const slide =
      this.mobileSlides[
        this.currentIndex
      ];


    const message =
      slide.querySelector(
        ANNOUNCEMENT_CONFIG
          .selectors
          .message
      );


    if (!message) {

      return;
    }


    /*
     * Show message.
     */

    slide.classList.add(
      ANNOUNCEMENT_CONFIG
        .classes
        .mobileActive
    );


    /*
     * Always begin at beginning
     * of sentence.
     */

    message.style.transform =
      "translate3d(0, 0, 0)";


    requestAnimationFrame(
      () => {

        if (
          !this.isMobileView()
        ) {

          return;
        }


        const styles =
          getComputedStyle(
            slide
          );


        const leftPadding =

          parseFloat(
            styles.paddingLeft
          ) || 0;


        const rightPadding =

          parseFloat(
            styles.paddingRight
          ) || 0;



        /*
         * Actual available text width.
         *
         * Your CSS X-button safe area is
         * automatically included here.
         */

        const availableWidth =

          Math.max(
            0,

            slide.clientWidth -

            leftPadding -

            rightPadding
          );



        /*
         * Calculate how much of the sentence
         * is hidden.
         */

        const scrollDistance =

          Math.max(
            0,

            message.scrollWidth -

            availableWidth
          );



        const mobile =
          ANNOUNCEMENT_CONFIG.mobile;



        /* ==================================================
           SHORT MESSAGE

           Fits entirely on screen.
           ================================================== */

        if (
          scrollDistance <=
          mobile.overflowTolerancePx
        ) {


          /*
           * Only one announcement?
           *
           * Leave it permanently.
           */

          if (
            this.mobileSlides.length <= 1
          ) {

            return;
          }


          this.restartTimer =

            setTimeout(
              () => {

                this.transitionToNextAnnouncement();

              },

              mobile.shortMessageDisplayMs +

              mobile.betweenMessagesPauseMs
            );


          return;
        }



        /* ==================================================
           LONG MESSAGE
           ================================================== */


        const movementTimeMs =

          (
            scrollDistance /

            mobile.scrollSpeedPxPerSec
          ) * 1000;



        const totalTimeMs =

          mobile.startPauseMs +

          movementTimeMs +

          mobile.endPauseMs;



        /*
         * Convert pause times into animation offsets.
         */

        const startOffset =

          mobile.startPauseMs /

          totalTimeMs;



        const endOffset =

          (
            mobile.startPauseMs +

            movementTimeMs
          ) /

          totalTimeMs;



        /*
         * Animation:
         *
         * 1. Beginning appears
         * 2. Pause
         * 3. Scroll left
         * 4. Ending appears
         * 5. Pause
         */

        this.mobileAnimation =

          message.animate(
            [

              {
                transform:
                  "translate3d(0, 0, 0)",

                offset: 0
              },


              {
                transform:
                  "translate3d(0, 0, 0)",

                offset:
                  startOffset
              },


              {
                transform:
                  `translate3d(-${scrollDistance}px, 0, 0)`,

                offset:
                  endOffset
              },


              {
                transform:
                  `translate3d(-${scrollDistance}px, 0, 0)`,

                offset: 1
              }

            ],

            {

              duration:
                totalTimeMs,

              easing:
                "linear",

              fill:
                "forwards"

            }
          );



        /*
         * Once text finishes:
         *
         * pause ->
         * quick transition to next message.
         */

        this.mobileAnimation
          .onfinish =
          () => {

            if (
              !this.isMobileView()
            ) {

              return;
            }


            this.restartTimer =

              setTimeout(
                () => {

                  this.transitionToNextAnnouncement();

                },

                mobile
                  .betweenMessagesPauseMs
              );
          };

      }
    );
  }



  /* ========================================================
     MOBILE #1 -> #2 QUICK TRANSITION
     ======================================================== */


  transitionToNextAnnouncement() {

    if (
      !this.isMobileView() ||
      !this.mobileSlides.length
    ) {

      return;
    }


    /*
     * One long announcement?
     *
     * Restart same message.
     */

    if (
      this.mobileSlides.length === 1
    ) {

      this.showMobileAnnouncement(
        0
      );

      return;
    }


    this.clearTimer(
      "restartTimer"
    );


    this.cancelAnimation(
      this.mobileAnimation
    );


    this.mobileAnimation =
      null;


    const currentSlide =

      this.mobileSlides[
        this.currentIndex
      ];



    const nextIndex =

      this.normalizeIndex(

        this.currentIndex + 1,

        this.mobileSlides.length
      );



    const nextSlide =

      this.mobileSlides[
        nextIndex
      ];



    const nextMessage =

      nextSlide.querySelector(
        ANNOUNCEMENT_CONFIG
          .selectors
          .message
      );



    /*
     * Prevent bar shrinking during transition.
     */

    this.lockMobileHeight();



    this.announcementBar
      .classList
      .add(
        ANNOUNCEMENT_CONFIG
          .classes
          .mobileSwitching
      );



    /*
     * Make next announcement visible.
     */

    nextSlide.classList.add(
      ANNOUNCEMENT_CONFIG
        .classes
        .mobileActive
    );



    /*
     * Next announcement begins at beginning.
     */

    if (nextMessage) {

      nextMessage.style.transform =
        "translate3d(0, 0, 0)";
    }



    const mobile =
      ANNOUNCEMENT_CONFIG.mobile;



    const token =
      ++this.mobileTransitionToken;



    /*
     * Current message exits LEFT.
     */

    const currentAnimation =

      currentSlide.animate(
        [

          {
            transform:
              "translate3d(0, 0, 0)"
          },


          {
            transform:
              "translate3d(-100%, 0, 0)"
          }

        ],

        {

          duration:
            mobile.transitionDurationMs,

          easing:
            mobile.transitionEasing,

          fill:
            "forwards"

        }
      );



    /*
     * Next message enters from RIGHT.
     */

    const nextAnimation =

      nextSlide.animate(
        [

          {
            transform:
              "translate3d(100%, 0, 0)"
          },


          {
            transform:
              "translate3d(0, 0, 0)"
          }

        ],

        {

          duration:
            mobile.transitionDurationMs,

          easing:
            mobile.transitionEasing,

          fill:
            "forwards"

        }
      );



    this.mobileTransitionAnimations = [

      currentAnimation,

      nextAnimation

    ];



    Promise.allSettled(
      [

        currentAnimation.finished,

        nextAnimation.finished

      ]
    ).then(
      () => {


        /*
         * Ignore outdated animation.
         */

        if (
          token !==
          this.mobileTransitionToken
        ) {

          return;
        }


        if (
          !this.isMobileView()
        ) {

          return;
        }


        /*
         * Remove old message.
         */

        currentSlide.classList.remove(
          ANNOUNCEMENT_CONFIG
            .classes
            .mobileActive
        );


        /*
         * Clean transition animations.
         */

        this.cancelAnimationList(
          this.mobileTransitionAnimations
        );


        /*
         * Update current index.
         */

        this.currentIndex =
          nextIndex;


        /*
         * Exit transition mode.
         */

        this.announcementBar
          .classList
          .remove(
            ANNOUNCEMENT_CONFIG
              .classes
              .mobileSwitching
          );


        /*
         * Start normal behavior
         * for next message.
         */

        this.showMobileAnnouncement(
          nextIndex
        );


        /*
         * Release height only after
         * browser finishes layout.
         */

        this.unlockMobileHeightAfterLayout();

      }
    );
  }



  /* ========================================================
     MOBILE SWIPE
     ======================================================== */


  previousAnnouncement() {

    if (
      !this.isMobileView() ||
      !this.mobileSlides.length
    ) {

      return;
    }


    /*
     * Cancel transition that may already be running.
     */

    this.cancelMobileAnimations();


    this.showMobileAnnouncement(

      this.normalizeIndex(

        this.currentIndex - 1,

        this.mobileSlides.length
      )
    );
  }



  nextAnnouncement() {

    if (
      !this.isMobileView() ||
      !this.mobileSlides.length
    ) {

      return;
    }


    this.cancelMobileAnimations();


    this.showMobileAnnouncement(

      this.normalizeIndex(

        this.currentIndex + 1,

        this.mobileSlides.length
      )
    );
  }



  handleTouchStart(event) {

    if (
      !this.isMobileView()
    ) {

      return;
    }


    this.touchStartX =

      event.touches[0]
        .clientX;


    this.touchEndX =

      this.touchStartX;
  }



  handleTouchMove(event) {

    if (
      !this.isMobileView()
    ) {

      return;
    }


    this.touchEndX =

      event.touches[0]
        .clientX;
  }



  handleTouchEnd() {

    if (
      !this.isMobileView()
    ) {

      return;
    }


    const difference =

      this.touchEndX -

      this.touchStartX;



    if (

      Math.abs(
        difference
      ) <

      ANNOUNCEMENT_CONFIG
        .interaction
        .swipeThresholdPx

    ) {

      return;
    }



    /* Swipe left */

    if (
      difference < 0
    ) {

      this.nextAnnouncement();

      return;
    }



    /* Swipe right */

    this.previousAnnouncement();
  }



  /* ========================================================
     RESET MOBILE
     ======================================================== */


  resetMobileTicker() {

    this.clearTimer(
      "restartTimer"
    );


    this.cancelMobileAnimations();

    this.unlockMobileHeight();


    if (!this.announcementBar) {

      return;
    }


    this.announcementBar
      .classList
      .remove(
        ANNOUNCEMENT_CONFIG
          .classes
          .mobileSwitching
      );


    this.getAllSlides()
      .forEach(
        (slide) => {

          slide.classList.remove(
            ANNOUNCEMENT_CONFIG
              .classes
              .mobileActive
          );

        }
      );


    this.querySelectorAll(
      ANNOUNCEMENT_CONFIG
        .selectors
        .message
    ).forEach(
      (message) => {

        message.style.animation =
          "";


        message.style.transform =
          "";


        message.style.removeProperty(
          "--mobile-announcement-duration"
        );

      }
    );


    this.announcementBar
      .style
      .transform =
      "";


    this.announcementBar
      .style
      .transition =
      "";
  }



  /* ========================================================
     DESKTOP SETUP

     Shows EXACTLY ONE announcement at a time.
     ======================================================== */


  setupDesktopTicker() {

    if (!this.announcementBar) {

      return;
    }


    this.clearTimer(
      "desktopTimer"
    );


    this.cancelDesktopAnimations();


    /*
     * IMPORTANT:
     *
     * Disable original theme animation before
     * creating our desktop slider.
     */

    this.disableDesktopThemeMotion();



    const allSlides =
      this.getAllSlides();



    this.desktopSlides =
      this.getUniqueSlides(
        allSlides
      );



    /*
     * Hide every duplicate generated by theme.
     */

    allSlides.forEach(
      (slide) => {

        slide.style.display =
          "none";


        slide.classList.remove(
          ANNOUNCEMENT_CONFIG
            .classes
            .desktopActive
        );

      }
    );



    /*
     * Override original multi-column desktop layout.
     */

    Object.assign(
      this.announcementBar.style,
      {

        display:
          "block",

        width:
          "100%",

        maxWidth:
          "100%",

        transform:
          "none",

        overflow:
          "hidden",

        position:
          "relative"

      }
    );



    this.desktopIndex =
      0;



    if (
      this.desktopSlides.length
    ) {

      this.showDesktopAnnouncement(
        0
      );
    }
  }



  /* ========================================================
     DESKTOP MESSAGE DISPLAY
     ======================================================== */


  showDesktopAnnouncement(
    index
  ) {

    if (
      this.isMobileView() ||
      !this.desktopSlides.length
    ) {

      return;
    }


    this.clearTimer(
      "desktopTimer"
    );


    this.desktopIndex =

      this.normalizeIndex(

        index,

        this.desktopSlides.length
      );



    /*
     * Hide everything first.
     */

    this.getAllSlides()
      .forEach(
        (slide) => {

          slide.style.display =
            "none";


          slide.classList.remove(
            ANNOUNCEMENT_CONFIG
              .classes
              .desktopActive
          );

        }
      );



    const slide =

      this.desktopSlides[
        this.desktopIndex
      ];



    /*
     * Show ONLY ONE desktop announcement.
     */

    Object.assign(
      slide.style,
      {

        display:
          "flex",

        width:
          "100%",

        maxWidth:
          "100%",

        justifyContent:
          "center",

        alignItems:
          "center",

        willChange:
          "transform"

      }
    );



    slide.classList.add(
      ANNOUNCEMENT_CONFIG
        .classes
        .desktopActive
    );



    /*
     * Only one announcement?
     * Leave it visible permanently.
     */

    if (
      this.desktopSlides.length <= 1
    ) {

      return;
    }



    /*
     * Wait before switching.
     */

    this.desktopTimer =

      setTimeout(
        () => {

          this.transitionDesktopAnnouncement();

        },

        ANNOUNCEMENT_CONFIG
          .desktop
          .displayTimeMs
      );
  }



  /* ========================================================
     DESKTOP #1 -> #2 TRANSITION

     This version uses translate3d() and disables the
     theme's original animations to prevent wavy movement.
     ======================================================== */


  transitionDesktopAnnouncement() {

    if (
      this.isMobileView() ||
      this.desktopSlides.length <= 1
    ) {
      return;
    }


    this.clearTimer(
      "desktopTimer"
    );


    /* ======================================================
      GET CURRENT / NEXT ANNOUNCEMENT
      ====================================================== */

    const currentSlide =
      this.desktopSlides[
        this.desktopIndex
      ];


    const nextIndex =
      this.normalizeIndex(
        this.desktopIndex + 1,
        this.desktopSlides.length
      );


    const nextSlide =
      this.desktopSlides[
        nextIndex
      ];


    const currentMessage =
      currentSlide.querySelector(
        ANNOUNCEMENT_CONFIG
          .selectors
          .message
      );


    const nextMessage =
      nextSlide.querySelector(
        ANNOUNCEMENT_CONFIG
          .selectors
          .message
      );


    /*
    * Safety fallback.
    */

    if (
      !currentMessage ||
      !nextMessage
    ) {

      this.desktopIndex =
        nextIndex;

      this.showDesktopAnnouncement(
        nextIndex
      );

      return;
    }



    /* ======================================================
      MEASURE EXACT CURRENT POSITION

      Important:
      We preserve the current slide's exact Y position
      instead of recalculating vertical centering.
      ====================================================== */

    const containerRect =
      this.announcementBar
        .getBoundingClientRect();


    const currentSlideRect =
      currentSlide
        .getBoundingClientRect();


    /*
    * Exact current vertical position relative
    * to announcement container.
    */

    const slideTop =
      currentSlideRect.top -
      containerRect.top;


    const slideHeight =
      currentSlideRect.height;


    /*
    * Exact horizontal distance for transition.
    */
    const currentMarginTop =
      parseFloat(
        getComputedStyle(currentSlide).marginTop
      ) || 0;

    const nextMarginTop =
      parseFloat(
        getComputedStyle(nextSlide).marginTop
      ) || 0;
    
    const travelDistance =
      containerRect.width;



    /* ======================================================
      LOCK BAR HEIGHT

      Prevents any vertical size change.
      ====================================================== */

    this.announcementBar.style.height =
      `${containerRect.height}px`;



    /* ======================================================
      FIX CURRENT SLIDE IN EXACT EXISTING POSITION
      ====================================================== */

    Object.assign(
      currentSlide.style,
      {

        display:
          "flex",

        position:
          "absolute",

        top:
          `${slideTop - currentMarginTop}px`,

        left:
          "0",

        width:
          "100%",

        height:
          `${slideHeight}px`,

        boxSizing:
          "border-box",

        justifyContent:
          "center",

        alignItems:
          "center"

      }
    );



    /* ======================================================
      PUT NEXT SLIDE AT EXACT SAME Y POSITION
      ====================================================== */

    Object.assign(
      nextSlide.style,
      {

        display:
          "flex",

        position:
          "absolute",

        top:
          `${slideTop - currentMarginTop}px`,

        left:
          "0",

        width:
          "100%",

        height:
          `${slideHeight}px`,

        boxSizing:
          "border-box",

        justifyContent:
          "center",

        alignItems:
          "center"

      }
    );



    /* ======================================================
      FORCE IDENTICAL VERTICAL TEXT METRICS

      This prevents a 1px jump if the browser calculates
      the two message line boxes slightly differently.
      ====================================================== */

    const currentMessageStyle =
      getComputedStyle(
        currentMessage
      );


    const fixedLineHeight =
      currentMessageStyle.lineHeight;


    if (
      fixedLineHeight &&
      fixedLineHeight !== "normal"
    ) {

      currentMessage.style.lineHeight =
        fixedLineHeight;


      nextMessage.style.lineHeight =
        fixedLineHeight;
    }



    /*
    * Only the TEXT gets animated.
    *
    * The slide itself NEVER moves vertically.
    */

    currentMessage.style.willChange =
      "transform";


    nextMessage.style.willChange =
      "transform";



    const desktop =
      ANNOUNCEMENT_CONFIG.desktop;


    const token =
      ++this.desktopTransitionToken;



    /* ======================================================
      CURRENT TEXT

      Moves horizontally LEFT.
      Y always remains exactly 0.
      ====================================================== */

    const currentAnimation =
      currentMessage.animate(
        [

          {
            transform:
              "translate3d(0px, 0px, 0px)"
          },

          {
            transform:
              `translate3d(-${travelDistance}px, 0px, 0px)`
          }

        ],
        {

          duration:
            desktop.transitionDurationMs,

          easing:
            desktop.transitionEasing,

          fill:
            "forwards"

        }
      );



    /* ======================================================
      NEXT TEXT

      Comes horizontally from RIGHT.
      Y always remains exactly 0.
      ====================================================== */

    const nextAnimation =
      nextMessage.animate(
        [

          {
            transform:
              `translate3d(${travelDistance}px, 0px, 0px)`
          },

          {
            transform:
              "translate3d(0px, 0px, 0px)"
          }

        ],
        {

          duration:
            desktop.transitionDurationMs,

          easing:
            desktop.transitionEasing,

          fill:
            "forwards"

        }
      );



    this.desktopTransitionAnimations = [

      currentAnimation,
      nextAnimation

    ];



    /* ======================================================
      AFTER TRANSITION
      ====================================================== */

    Promise.allSettled(
      [

        currentAnimation.finished,
        nextAnimation.finished

      ]
    ).then(
      () => {


        /*
        * Ignore old animation callback.
        */

        if (
          token !==
          this.desktopTransitionToken
        ) {

          return;
        }


        if (
          this.isMobileView()
        ) {

          return;
        }



        /*
        * Hide old slide BEFORE removing animation.
        */

        currentSlide.style.display =
          "none";



        /*
        * Remove Web Animations.
        */

        this.cancelAnimationList(
          this.desktopTransitionAnimations
        );



        /* ==================================================
          RESET MESSAGE STYLES
          ================================================== */

        currentMessage.style.transform =
          "";

        currentMessage.style.willChange =
          "";

        currentMessage.style.lineHeight =
          "";


        nextMessage.style.transform =
          "";

        nextMessage.style.willChange =
          "";

        nextMessage.style.lineHeight =
          "";



        /* ==================================================
          RESET TEMPORARY SLIDE POSITIONING
          ================================================== */

        this.resetSlideInlineStyles(
          currentSlide
        );


        this.resetSlideInlineStyles(
          nextSlide
        );


        /*
        * We added box-sizing specifically for transition.
        */

        currentSlide.style.boxSizing =
          "";

        nextSlide.style.boxSizing =
          "";



        /*
        * Unlock announcement-bar height.
        */

        this.announcementBar.style.height =
          "";



        /* ==================================================
          UPDATE CURRENT MESSAGE
          ================================================== */

        this.desktopIndex =
          nextIndex;



        /*
        * Return to normal desktop state and
        * start the timer for the new announcement.
        */

        this.showDesktopAnnouncement(
          nextIndex
        );

      }
    );
  }



  /* ========================================================
     RESET DESKTOP
     ======================================================== */


  resetDesktopTicker() {

    this.clearTimer(
      "desktopTimer"
    );


    this.cancelDesktopAnimations();



    this.getAllSlides()
      .forEach(
        (slide) => {

          slide.classList.remove(
            ANNOUNCEMENT_CONFIG
              .classes
              .desktopActive
          );


          this.resetSlideInlineStyles(
            slide
          );

        }
      );



    this.resetContainerInlineStyles();



    /*
     * Restore the theme's original animation styles
     * when leaving desktop mode.
     */

    this.restoreDesktopThemeMotion();
  }



  /* ========================================================
     CLOSE BUTTON
     ======================================================== */


  hideForOneDay() {

    this.clearAllTimers();

    this.cancelAllAnimations();



    const hiddenUntil =

      Date.now() +

      ANNOUNCEMENT_CONFIG
        .close
        .hideDurationMs;



    localStorage.setItem(

      ANNOUNCEMENT_CONFIG
        .close
        .storageKey,

      hiddenUntil
    );



    /*
     * Hide announcement component.
     */

    this.style.display =
      "none";



    /*
     * Hide Shopify section.
     */

    const section =

      this.closest(
        ".shopify-section, .wt-announcement"
      );



    if (section) {

      section.style.display =
        "none";
    }



    /*
     * Notify other theme code that
     * announcement height changed.
     */

    document.dispatchEvent(

      new CustomEvent(
        "announcement-bar:change",
        {

          detail: {
            visible: false
          }

        }
      )
    );



    /*
     * Update page header position.
     */

    if (

      typeof window
        .syncPageHeaderTop ===
      "function"

    ) {

      window.syncPageHeaderTop();
    }
  }
}



/* ==========================================================
   REGISTER CUSTOM ELEMENT

   Shopify Theme Editor can reload this section,
   so prevent duplicate registration errors.
   ========================================================== */

if (
  !customElements.get(
    "announcement-bar"
  )
) {

  customElements.define(
    "announcement-bar",
    AnnouncementBar
  );
}